import {
  WSDataItemProps,
  WSTableCellProps
} from "@wingspanhq/fe-component-library";
import {
  PayeeRequirement,
  PayerRow,
  RequirementType
} from "@wingspanhq/wingspan-api-sdk";
import { WSQueryKey } from "@ws-react-query";

import { selectorSearchRowName } from "../../shared/selectors/selectorSearchRowName";

import { IDENTITY_VERIFICATION_REQUIREMENT_EVENT } from "./modules/IdentityVerification/configs/utils";
import { INSURANCE_COVERAGE_REQUIREMENT_EVENT } from "./modules/InsuranceCoverage/configs/utils";
import { SHARED_FILE_REQUIREMENT_EVENT } from "./modules/SharedFile/configs/utils";
import { SIGNATURE_REQUIREMENT_EVENT } from "./modules/Signature/configs/utils";
import {
  RequirementDataSourceState,
  selectorRequirementDataSourceState
} from "./selectors/selectorRequirementDataSourceState";
import { selectorRequirementEngagementsSize } from "./selectors/selectorRequirementEngagementsSize";
import {
  CellsConfig,
  ContextItem,
  CustomRequirement,
  ExtendedRequirementTableItemData,
  RequirementAdditionalData,
  RequirementContextType,
  RequirementFieldsConfig,
  RequirementTableRowConfig,
  SupportedRequirementType,
  TableCellProps
} from "./types";

export const ALL_REQUIREMENT_EVENTS = {
  OverrideStatus: "OverrideStatus",
  ResetStatus: "ResetStatus",
  ExtendExpirationDate: "ExtendExpirationDate",
  Renew: "Renew",
  ...SIGNATURE_REQUIREMENT_EVENT,
  ...SHARED_FILE_REQUIREMENT_EVENT,
  ...IDENTITY_VERIFICATION_REQUIREMENT_EVENT,
  ...INSURANCE_COVERAGE_REQUIREMENT_EVENT
} as const;

export type RequirementEvent =
  (typeof ALL_REQUIREMENT_EVENTS)[keyof typeof ALL_REQUIREMENT_EVENTS];

export const LIST_OF_FIELDS: Array<
  keyof Omit<
    RequirementFieldsConfig<any>,
    "isLoading" | "isFetching" | "dataUpdatedAt" | "getAdditionalData"
  >
> = [
  "requirementName",
  "requirementInstructions",
  "requirementDetailsProgressWidget",
  "isActionRequired",
  "requirementInstructionsWidgets",
  "requirementStatusAlert",
  "requirementDetails",
  "requirementBasicInformation",
  "requirementDetailsWidgets",
  "requirementAdditionalContent",
  "toolbarActions",
  "nameCell",
  "typeCell",
  "statusCell",
  "clientsCell",
  "engagementsCell"
];

export const sharedRequirementsQueriesConfig = {
  refetchOnMount: (query: any) => {
    return query.state.isInvalidated;
  },
  retry: false,
  retryOnMount: true
};

export const buildConfigFields = <
  AdditionalData extends RequirementAdditionalData,
  RT extends SupportedRequirementType
>(props: {
  payerConfig: CellsConfig<PayeeRequirement, ContextItem, AdditionalData>;
  payeeConfig: CellsConfig<PayeeRequirement, ContextItem, AdditionalData>;
  getAdditionalData: (
    requirement: PayeeRequirement,
    context: ContextItem
  ) => AdditionalData | undefined;
  /**
   * Whether this requirement type resolves its status from a data-source
   * document (a signature request, a background check, an insurance monitor…).
   *
   * When it does, a missing document means the document read failed and the
   * status is genuinely unknown, so the status cell shows an error rather than a
   * misleading pill. When it does not — `Registration` and `PayoutMethod` carry
   * their status on the requirement itself — an absent document is the normal
   * case and the type's own status resolver must still run.
   *
   * Declared explicitly, never inferred from a falsy `getAdditionalData`: a type
   * with no document is indistinguishable from a failed read otherwise, and
   * reading absence as failure is what blanked the "Sign up" and "Payout method"
   * statuses.
   */
  hasDataSource: boolean;
  /**
   * The cache key of a requirement's data-source document, so a missing
   * document can be attributed to a pending read rather than a failed one.
   *
   * Required in practice for every `hasDataSource` type — omitting it makes a
   * missing document read as an error again, which is the bug this exists to
   * prevent.
   */
  getDataSourceQueryKey?: (dataSourceId: string) => WSQueryKey;
}) => {
  const errorCell: TableCellProps = {
    text: "--",
    secondaryText: "error loading",
    secondaryTextColor: "amber400"
  };

  /**
   * Shown while the document read is still in flight. Deliberately neutral: the
   * loading affordance is the section shimmer driven by the config's
   * `isLoading`/`isFetching`, and an amber "error loading" here is a lie until
   * the read has actually settled.
   */
  const pendingCell: TableCellProps = {
    text: "--"
  };

  /**
   * A status cell with no document: amber only once the read has settled on a
   * failure, neutral while it is still pending.
   */
  const unresolvedStatusCell = (requirement: PayeeRequirement) => {
    if (!props.getDataSourceQueryKey) {
      return errorCell;
    }

    return selectorRequirementDataSourceState({
      requirement,
      getQueryKey: props.getDataSourceQueryKey
    }) === RequirementDataSourceState.Error
      ? errorCell
      : pendingCell;
  };

  return () => {
    const fields: RequirementTableRowConfig[RT] =
      {} as RequirementTableRowConfig[RT];

    LIST_OF_FIELDS.forEach(key => {
      const isUnresolvableStatus = (document: AdditionalData | undefined) =>
        props.hasDataSource && !document && key === "statusCell";

      fields[key] = {
        [RequirementContextType.Payer]: (requirement, context) => {
          const document = props.getAdditionalData(requirement, context);

          if (isUnresolvableStatus(document)) {
            return unresolvedStatusCell(requirement);
          }

          return props.payerConfig[key]?.(
            requirement,
            context,
            document!
          ) as any;
        },
        [RequirementContextType.Payee]: (requirement, context) => {
          const document = props.getAdditionalData(requirement, context);

          if (isUnresolvableStatus(document)) {
            return unresolvedStatusCell(requirement);
          }

          return props.payeeConfig[key]?.(
            requirement,
            context,
            document!
          ) as any;
        }
      };
    });

    return fields;
  };
};

export function getIsCustomRequirement(
  requirement: ExtendedRequirementTableItemData
): requirement is CustomRequirement {
  // Custom rows are client-built cell props; real v3 instances never carry them.
  return "nameCell" in requirement;
}

export function getRequirementListKey(
  requirement: ExtendedRequirementTableItemData,
  index: number
) {
  if (getIsCustomRequirement(requirement)) {
    return index.toString();
  }

  return requirement.id ?? String(index);
}

export function getIsGeneralRequirement(requirement: PayeeRequirement) {
  return (
    !!requirement.type &&
    [
      RequirementType.REGISTRATION,
      RequirementType.PAYOUT_METHOD,
      RequirementType.TAX_VERIFICATION
    ].includes(requirement.type)
  );
}

export function getRequirementPayerDataItemProps(
  requirement: PayeeRequirement,
  payer?: PayerRow
): WSDataItemProps {
  const payerName = payer ? selectorSearchRowName(payer) : undefined;

  return {
    label: "Clients",
    value: payerName,
    secondaryText: `${selectorRequirementEngagementsSize(
      requirement
    )} engagement(s)`
  };
}

export function getRequirementPayerTableCellProps(
  requirement: PayeeRequirement,
  payer?: PayerRow
): WSTableCellProps {
  const payerName = payer ? selectorSearchRowName(payer) : undefined;

  return {
    text: payerName,
    secondaryText: `${selectorRequirementEngagementsSize(
      requirement
    )} engagement(s)`
  };
}

export function getRequirementEngagementsDataItemProps(
  requirement: PayeeRequirement
): WSDataItemProps {
  return {
    label: "Engagements",
    value: selectorRequirementEngagementsSize(requirement)
  };
}

export function getRequirementEngagementsTableCellProps(
  requirement: PayeeRequirement
): WSTableCellProps {
  return {
    text: String(selectorRequirementEngagementsSize(requirement))
  };
}

export { sortRequirementsByPriority } from "./utils/sortRequirementsByPriority";
